/**
 * Data View - Admin
 * Query and manage data sources with visualization
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database,
  Search,
  Play,
  Download,
  RefreshCw,
  Filter,
  Table,
  BarChart3,
  FileJson,
  Settings,
  Plus,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface DataSource {
  id: string;
  name: string;
  type: "vector" | "relational" | "document" | "graph";
  size: string;
  records: number;
  status: "connected" | "disconnected" | "syncing";
  lastSync: string;
  config: any;
}

interface QueryResult {
  id: string;
  timestamp: string;
  query: string;
  duration: number;
  rows: number;
  columns: string[];
  data: any[];
}

const MOCK_DATA_SOURCES: DataSource[] = [
  {
    id: "1",
    name: "Vector Store - OpenAI",
    type: "vector",
    size: "2.4 GB",
    records: 156789,
    status: "connected",
    lastSync: "Just now",
    config: { embeddingModel: "text-embedding-3-large" },
  },
  {
    id: "2",
    name: "PostgreSQL - Main DB",
    type: "relational",
    size: "45.8 GB",
    records: 1245678,
    status: "connected",
    lastSync: "2m ago",
    config: { host: "db1.internal", port: 5432 },
  },
  {
    id: "3",
    name: "MongoDB - Documents",
    type: "document",
    size: "12.3 GB",
    records: 45678,
    status: "syncing",
    lastSync: "Syncing...",
    config: { cluster: "prod-cluster" },
  },
  {
    id: "4",
    name: "Neo4j - Knowledge Graph",
    type: "graph",
    size: "5.6 GB",
    records: 89012,
    status: "connected",
    lastSync: "10m ago",
    config: { uri: "bolt://neo4j.internal" },
  },
];

const MOCK_QUERY_RESULTS: QueryResult[] = [
  {
    id: "1",
    timestamp: "2025-12-29T18:00:00Z",
    query: "SELECT * FROM users WHERE created_at > NOW() - INTERVAL '7 days'",
    duration: 45,
    rows: 156,
    columns: ["id", "name", "email", "created_at"],
    data: Array.from({ length: 5 }, (_, i) => ({
      id: i + 1,
      name: `User ${i + 1}`,
      email: `user${i + 1}@example.com`,
      created_at: new Date().toISOString(),
    })),
  },
];

export function DataView() {
  const { addLogEntry } = useDashboardStore();
  const [sources, setSources] = useState<DataSource[]>([]);
  const [queryResults, setQueryResults] = useState<QueryResult[]>([]);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");
  const [expandedSource, setExpandedSource] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setSources(MOCK_DATA_SOURCES);
      setQueryResults(MOCK_QUERY_RESULTS);
    } catch (error) {
      console.error("Failed to load data:", error);
    }
  };

  const executeQuery = async () => {
    if (!query.trim()) return;
    
    setIsExecuting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const newResult: QueryResult = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        query,
        duration: Math.floor(Math.random() * 200) + 50,
        rows: Math.floor(Math.random() * 100) + 10,
        columns: ["id", "value", "timestamp"],
        data: Array.from({ length: 5 }, (_, i) => ({
          id: i + 1,
          value: Math.floor(Math.random() * 1000),
          timestamp: new Date().toISOString(),
        })),
      };
      
      setQueryResults([newResult, ...queryResults]);
      setQuery("");
      
      addLogEntry({
        category: "data",
        action: "Query Executed",
        details: `Executed query in ${newResult.duration}ms`,
        severity: "info",
        source: "data",
      });
    } catch (error) {
      console.error("Failed to execute query:", error);
    } finally {
      setIsExecuting(false);
    }
  };

  const syncSource = async (id: string) => {
    try {
      setSources(s => s.map(s => 
        s.id === id ? { ...s, status: "syncing" as const } : s
      ));
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSources(s => s.map(s => 
        s.id === id ? { ...s, status: "connected" as const, lastSync: "Just now" } : s
      ));
      
      addLogEntry({
        category: "data",
        action: "Source Synced",
        details: `Data source ${id} synced`,
        severity: "info",
        source: "data",
      });
    } catch (error) {
      console.error("Failed to sync source:", error);
    }
  };

  const getTypeColor = (type: DataSource["type"]) => {
    switch (type) {
      case "vector": return "text-purple-500 bg-purple-500/10";
      case "relational": return "text-blue-500 bg-blue-500/10";
      case "document": return "text-green-500 bg-green-500/10";
      case "graph": return "text-orange-500 bg-orange-500/10";
    }
  };

  const totalRecords = sources.reduce((sum, s) => sum + s.records, 0);
  const totalSize = sources.reduce((sum, s) => {
    const size = parseFloat(s.size);
    const unit = s.size.split(" ")[1];
    return sum + (unit === "TB" ? size * 1024 : unit === "GB" ? size : size / 1024);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Query and manage data sources</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">
            <Plus className="h-4 w-4" />
            Add Source
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Database className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{sources.length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Data Sources</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <FileJson className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{(totalRecords / 1000000).toFixed(1)}M</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Records</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
              <BarChart3 className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalSize.toFixed(1)} GB</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Size</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/10">
              <RefreshCw className="h-5 w-5 text-cyan-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{queryResults.length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Queries Today</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Data Sources Panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Data Sources</h2>
            <button className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors cursor-pointer">
              <Filter className="h-4 w-4" />
            </button>
          </div>
          
          <div className="space-y-2">
            {sources.map((source) => (
              <motion.div
                key={source.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => {
                  setSelectedSource(source.id);
                  setExpandedSource(expandedSource === source.id ? null : source.id);
                }}
                className={cn(
                  "rounded-lg border p-4 cursor-pointer transition-all",
                  selectedSource === source.id
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.5)]"
                    : "border-[hsl(var(--border))] hover:border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Database className={cn("h-4 w-4", getTypeColor(source.type).split(" ")[0])} />
                    <h3 className="font-medium text-sm">{source.name}</h3>
                  </div>
                  {expandedSource === source.id ? (
                    <ChevronDown className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  )}
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <span className={cn("rounded px-2 py-0.5 text-xs font-medium", getTypeColor(source.type))}>
                    {source.type}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                    <div className={cn(
                      "h-2 w-2 rounded-full",
                      source.status === "connected" ? "bg-green-500" : source.status === "syncing" ? "bg-blue-500 animate-pulse" : "bg-gray-500"
                    )} />
                    {source.status}
                  </span>
                </div>

                <AnimatePresence>
                  {expandedSource === source.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-2 pt-2 border-t border-[hsl(var(--border))]"
                    >
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-[hsl(var(--muted-foreground))]">Size</p>
                          <p className="font-medium">{source.size}</p>
                        </div>
                        <div>
                          <p className="text-[hsl(var(--muted-foreground))]">Records</p>
                          <p className="font-medium">{source.records.toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Last sync: {source.lastSync}</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          syncSource(source.id);
                        }}
                        disabled={source.status === "syncing"}
                        className="flex items-center justify-center gap-2 w-full rounded-lg bg-[hsl(var(--muted))] py-2 text-xs font-medium hover:bg-[hsl(var(--muted))/.8] disabled:opacity-50 transition-colors cursor-pointer"
                      >
                        <RefreshCw className={cn("h-3 w-3", source.status === "syncing" && "animate-spin")} />
                        {source.status === "syncing" ? "Syncing..." : "Sync Now"}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Query Panel */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Query</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("table")}
                className={cn(
                  "rounded-lg p-2 transition-colors",
                  viewMode === "table" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "hover:bg-[hsl(var(--muted))]"
                )}
              >
                <Table className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("chart")}
                className={cn(
                  "rounded-lg p-2 transition-colors",
                  viewMode === "chart" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "hover:bg-[hsl(var(--muted))]"
                )}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Query Input */}
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="rounded bg-[hsl(var(--muted))] px-2 py-1 text-xs font-mono">SELECT</span>
                <span className="rounded bg-[hsl(var(--muted))] px-2 py-1 text-xs font-mono">FROM</span>
                <span className="rounded bg-[hsl(var(--muted))] px-2 py-1 text-xs font-mono">WHERE</span>
              </div>
            </div>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter your SQL or query here..."
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] resize-none min-h-[120px]"
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex gap-2">
                <button className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors cursor-pointer">
                  <Settings className="h-4 w-4" />
                </button>
                <button className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors cursor-pointer">
                  <Filter className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={executeQuery}
                disabled={!query.trim() || isExecuting}
                className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] disabled:opacity-50 hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
              >
                {isExecuting ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Execute Query
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Query Results */}
          {queryResults.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
              <div className="flex items-center justify-between border-b border-[hsl(var(--border))] p-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold">Results</h3>
                  <span className="rounded bg-[hsl(var(--muted))] px-2 py-0.5 text-xs">
                    {queryResults[0].rows} rows
                  </span>
                  <span className="rounded bg-[hsl(var(--muted))] px-2 py-0.5 text-xs">
                    {queryResults[0].duration}ms
                  </span>
                </div>
                <button className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors cursor-pointer">
                  <Download className="h-4 w-4" />
                </button>
              </div>

              {viewMode === "table" ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)]">
                      <tr>
                        {queryResults[0].columns.map((col) => (
                          <th key={col} className="px-4 py-2 text-left font-medium">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queryResults[0].data.map((row, i) => (
                        <tr key={i} className="border-b border-[hsl(var(--border))]">
                          {queryResults[0].columns.map((col) => (
                            <td key={col} className="px-4 py-2">
                              {row[col]?.toString() || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-[hsl(var(--muted-foreground))]">
                  <BarChart3 className="mx-auto h-12 w-12 mb-4" />
                  <p>Chart view will display query results as visualizations</p>
                </div>
              )}
            </div>
          )}

          {/* Query History */}
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <h3 className="font-semibold mb-3">Recent Queries</h3>
            <div className="space-y-2">
              {queryResults.slice(0, 3).map((result) => (
                <div
                  key={result.id}
                  onClick={() => setQuery(result.query)}
                  className="rounded-lg bg-[hsl(var(--muted))] p-3 cursor-pointer hover:bg-[hsl(var(--muted))]/.8 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <code className="text-xs font-mono truncate flex-1">{result.query}</code>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
                    <span>{result.rows} rows</span>
                    <span>{result.duration}ms</span>
                    <span>{new Date(result.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
